/**
=========================================================
* Argon Dashboard 2 PRO MUI - v3.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-mui
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { forwardRef } from 'react';

// prop-types is a library for typechecking of props
import PropTypes from 'prop-types';

// Custom styles for ArgonInput
import ArgonInputRoot from 'components/ArgonInput/ArgonInputRoot';

// Argon Dashboard 2 PRO MUI context
import { useArgonController } from 'context';
import ArgonTypography from 'components/ArgonTypography';
import ArgonBox from 'components/ArgonBox';
import { PulseLoader } from 'react-spinners';
import TooltipWrapper from 'components/TooltipWrapper';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const ArgonInput = forwardRef(
  (
    {
      size = 'medium',
      error = false,
      success = false,
      helperText,
      helperTextColor,
      disabled = false,
      loading = false,
      style,
      label,
      tooltip,
      textColor,
      required,
      ...rest
    },
    ref
  ) => {
    const [controller] = useArgonController();
    const { darkMode } = controller;

    return (
      <>
        {(label || tooltip) && (
          <ArgonBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
            <TooltipWrapper tooltip={tooltip}>
              <ArgonTypography
                variant="caption"
                fontWeight="bold"
                textTransform="capitalize"
                color={textColor}
              >
                {label}&nbsp;
                {required && <span style={{ color: 'red' }}>*</span>}
                {tooltip && (
                  <ArgonBox ml={1} display="inline-block">
                    <ArgonTypography variant="regular">
                      <InfoOutlinedIcon color="secondary" />
                    </ArgonTypography>
                  </ArgonBox>
                )}
              </ArgonTypography>
            </TooltipWrapper>
          </ArgonBox>
        )}

        <ArgonInputRoot
          endAdornment={
            loading && (
              // <ArgonBox>
              <PulseLoader size="4px" color="#36d7b7" />
              // </ArgonBox>
            )
          }
          {...rest}
          style={style}
          ref={ref}
          ownerState={{ size, error, success, disabled, darkMode }}
        />
        {helperText && (
          <ArgonTypography variant="caption" color={helperTextColor || 'error'}>
            <ArgonBox color={helperTextColor || 'error'} ml={0.5} mb={1}>
              {helperText}
            </ArgonBox>
          </ArgonTypography>
        )}
      </>
    );
  }
);

// Typechecking props for the ArgonInput
ArgonInput.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  error: PropTypes.bool,
  success: PropTypes.bool,
  disabled: PropTypes.bool,
  helperText: PropTypes.string,
  helperTextColor: PropTypes.string,
  loading: PropTypes.bool,
  style: PropTypes.object
};

export default ArgonInput;
